// Informe PDF de una emergencia médica (una sola emergencia, todos sus datos).
// Se genera en el navegador con jsPDF; no requiere permisos extra porque sólo
// contiene lo que el usuario ya ve en el listado y en el detalle.
import { IEmergencia, IValoresEventoEmergencia } from 'modelos/src';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { situacionDe, presentacionSituacion } from '../estado-emergencia';
import { calcDiff, fechaHoraEmergencia } from './emergencia.service';

function calcDiffTexto(f1?: string, f2?: string): string {
  return calcDiff(f1, f2) ?? '';
}

const COLOR_HEADER_DARK: [number, number, number] = [30, 45, 64];
const COLOR_LABEL: [number, number, number] = [80, 80, 80];
const COLOR_LINE: [number, number, number] = [210, 215, 220];

/** Filtra las filas que no tienen valor. */
function filas(rows: [string, string][]): [string, string][] {
  return rows.filter(([, v]) => v && v.trim() !== '');
}

function seccion(
  doc: jsPDF,
  y: number,
  titulo: string,
  rows: [string, string][],
): number {
  const filtradas = filas(rows);
  if (!filtradas.length) return y;

  autoTable(doc, {
    startY: y,
    head: [[{ content: titulo, colSpan: 2 }]],
    body: filtradas,
    styles: {
      fontSize: 9,
      cellPadding: { top: 3, bottom: 3, left: 5, right: 5 },
      lineColor: COLOR_LINE,
      lineWidth: 0.2,
      textColor: [40, 40, 40],
    },
    headStyles: {
      fillColor: COLOR_HEADER_DARK,
      textColor: [255, 255, 255],
      fontSize: 9.5,
      fontStyle: 'bold',
      cellPadding: { top: 4, bottom: 4, left: 5, right: 5 },
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 58, textColor: COLOR_LABEL },
    },
    alternateRowStyles: { fillColor: [248, 249, 250] },
    margin: { left: 14, right: 14 },
  });

  return (doc as any).lastAutoTable.finalY + 5;
}

export function descargarInformePdfEmergencia(emergencia: IEmergencia): void {
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.width;
  let y = 0;

  // ── Banda de cabecera ────────────────────────────────────────────────────
  doc.setFillColor(...COLOR_HEADER_DARK);
  doc.rect(0, 0, pageW, 26, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`Emergencia médica N.º ${emergencia.codigo ?? ''}`, 14, 11);

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  const situacion = presentacionSituacion(situacionDe(emergencia)).label;
  const estado = emergencia.ultimoEventoEmergencia?.estado ?? '';
  doc.text(
    `Situación: ${situacion}   |   Estado: ${estado}   |   Generado: ${fechaHoraEmergencia(new Date())}`,
    14,
    20,
  );

  y = 32;
  doc.setTextColor(40, 40, 40);

  // ── Datos generales ──────────────────────────────────────────────────────
  y = seccion(doc, y, 'Datos Generales', [
    ['Fecha de creación', fechaHoraEmergencia(emergencia.fechaCreacion)],
    ['Solicitante', emergencia.solicitante ?? ''],
    ['Teléfono solicitante', emergencia.telefono ?? ''],
    ['Dirección', emergencia.direccion ?? ''],
    ['Código de apertura', emergencia.prioridadApertura ?? ''],
    ['Código de cierre', emergencia.prioridadCierre ?? ''],
    ['Observaciones', emergencia.observaciones ?? ''],
    [
      'Reasignaciones',
      emergencia.cantidadReasignaciones
        ? String(emergencia.cantidadReasignaciones)
        : '',
    ],
  ]);

  // ── Paciente ─────────────────────────────────────────────────────────────
  const pac = emergencia.destinatarioAsistencia;
  if (pac) {
    const nombreCompleto = [pac.nombre, pac.apellido].filter(Boolean).join(' ');
    y = seccion(doc, y, 'Paciente', [
      ['Nombre', nombreCompleto],
      ['DNI', pac.dni ?? ''],
      ['Edad', pac.edad != null ? String(pac.edad) : ''],
      ['Sexo', pac.sexo ?? ''],
      ['Obra social', pac.obraSocial ?? ''],
      ['Teléfono', pac.telefono ?? ''],
      ['Email', pac.email ?? ''],
    ]);
  }

  // ── Datos medicos ────────────────────────────────────────────────────────
  const em = emergencia.emergenciaMedica;
  if (em) {
    const sv = em.signosVitales;
    const tr = em.traslado;

    const filasMedicas: [string, string][] = [
      ['Síntomas de apertura', (em.sintomasApertura ?? []).join(', ')],
      ['Síntomas de cierre', (em.sintomasCierre ?? []).join(', ')],
      ['Diagnóstico', em.diagnostico ?? ''],
      ['Tratamiento', em.tratamiento ?? ''],
      ['Medicación', em.medicacion ?? ''],
      ['Llegada a hospital', fechaHoraEmergencia(em.fechaLlegadaHospital)],
    ];

    if (sv) {
      filasMedicas.push(
        [
          'T.A. (mmHg)',
          sv.tensionArterial != null ? String(sv.tensionArterial) : '',
        ],
        [
          'Frec. respiratoria (irpm)',
          sv.frecuenciaRespiratoria != null
            ? String(sv.frecuenciaRespiratoria)
            : '',
        ],
        [
          'Frec. cardíaca (lpm)',
          sv.frecuenciaCardiaca != null ? String(sv.frecuenciaCardiaca) : '',
        ],
        [
          'Saturación O2 (%)',
          sv.saturacionOxigeno != null ? String(sv.saturacionOxigeno) : '',
        ],
        [
          'Temperatura (C)',
          sv.temperatura != null ? String(sv.temperatura) : '',
        ],
      );
    }

    if (tr) {
      filasMedicas.push(
        ['Requiere traslado', tr.traslado ? 'Si' : 'No'],
        [
          'Acepta traslado',
          tr.aceptaTraslado != null ? (tr.aceptaTraslado ? 'Si' : 'No') : '',
        ],
        ['Dirección de traslado', tr.direccion ?? ''],
      );
    }

    y = seccion(doc, y, 'Datos Médicos', filasMedicas);
  }

  // ── Tiempos operativos ───────────────────────────────────────────────────
  y = seccion(doc, y, 'Tiempos Operativos', [
    [
      'Creación de la emergencia',
      fechaHoraEmergencia(emergencia.fechaCreacion),
    ],
    [
      'Primera asignación',
      fechaHoraEmergencia(emergencia.fechaPrimeraAsignacion),
    ],
    [
      'Salida de la base de operaciones',
      fechaHoraEmergencia(emergencia.fechaSalida),
    ],
    ['Llegada a destino', fechaHoraEmergencia(emergencia.fechaLlegadaDestino)],
    ['Finalización', fechaHoraEmergencia(emergencia.fechaFinalizacion)],
    [
      'Tiempo hasta 1ra asignación',
      calcDiffTexto(
        emergencia.fechaCreacion,
        emergencia.fechaPrimeraAsignacion,
      ),
    ],
    [
      'Tiempo entre asignación y salida de la base de operaciones',
      calcDiffTexto(emergencia.fechaPrimeraAsignacion, emergencia.fechaSalida),
    ],
    [
      'Tiempo entre salida de la base de operaciones y llegada a destino',
      calcDiffTexto(emergencia.fechaSalida, emergencia.fechaLlegadaDestino),
    ],
    [
      'Tiempo total',
      calcDiffTexto(emergencia.fechaCreacion, emergencia.fechaFinalizacion),
    ],
  ]);

  // ── Ultimo evento / asignacion ───────────────────────────────────────────
  const ev = emergencia.ultimoEventoEmergencia;
  if (ev) {
    const val = (ev.valores as IValoresEventoEmergencia) ?? {};
    const det = ev.detallesEmergencias;

    y = seccion(doc, y, 'Último Estado y Asignaciones', [
      ['Estado', ev.estado ?? ''],
      ['Fecha del evento', fechaHoraEmergencia(ev.fechaCreacion)],
      ['Observaciones', val.observaciones ?? ''],
      ['Motivo de cancelación', val.motivoCancelacion ?? ''],
      ['Motivo de reasignación', val.motivoReasignacion ?? ''],
      ['Diagnóstico del evento', val.diagnostico ?? ''],
      ['Responsable', det?.usuarioResponsable?.datosPersonales?.nombre ?? ''],
      ['Vehículo', det?.movilAsignado?.identificacion ?? ''],
      ['Chofer', det?.chofer?.usuario ?? ''],
      [
        'Médicos',
        (det?.medicos ?? [])
          .map((m) =>
            m.matricula ? `${m.nombre} (Mat. ${m.matricula})` : m.nombre,
          )
          .join(', '),
      ],
      ['Enfermeros', (det?.enfermeros ?? []).map((e) => e.nombre).join(', ')],
      ['Hospital', det?.hospital?.identificacion ?? ''],
    ]);
  }

  // ── Pie de pagina ────────────────────────────────────────────────────────
  const totalPags = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPags; i++) {
    doc.setPage(i);

    // Linea superior del pie
    doc.setDrawColor(...COLOR_LINE);
    doc.setLineWidth(0.3);
    doc.line(
      14,
      doc.internal.pageSize.height - 14,
      pageW - 14,
      doc.internal.pageSize.height - 14,
    );

    doc.setFontSize(7.5);
    doc.setTextColor(140, 140, 140);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Emergencia N.º ${emergencia.codigo ?? ''}  -  Página ${i} de ${totalPags}`,
      14,
      doc.internal.pageSize.height - 8,
    );
    doc.text(
      fechaHoraEmergencia(new Date()),
      pageW - 14,
      doc.internal.pageSize.height - 8,
      { align: 'right' },
    );
  }

  doc.save(`emergencia_${emergencia.codigo}.pdf`);
}
